export const ALL_COUNTRIES_VALUE = 'all';

export interface ContestCountryOption {
  code: string;
  region: ContestCountryRegion;
}

export type ContestCountryRegion =
  | 'north_america'
  | 'central_america'
  | 'caribbean'
  | 'south_america'
  | 'europe'
  | 'asia_pacific';

export const CONTEST_COUNTRY_OPTIONS: ContestCountryOption[] = [
  { code: 'us', region: 'north_america' },
  { code: 'ca', region: 'north_america' },
  { code: 'mx', region: 'north_america' },
  { code: 'gt', region: 'central_america' },
  { code: 'hn', region: 'central_america' },
  { code: 'sv', region: 'central_america' },
  { code: 'ni', region: 'central_america' },
  { code: 'cr', region: 'central_america' },
  { code: 'pa', region: 'central_america' },
  { code: 'jm', region: 'caribbean' },
  { code: 'do', region: 'caribbean' },
  { code: 'pr', region: 'caribbean' },
  { code: 'co', region: 'south_america' },
  { code: 'ec', region: 'south_america' },
  { code: 'pe', region: 'south_america' },
  { code: 've', region: 'south_america' },
  { code: 'ar', region: 'south_america' },
  { code: 'cl', region: 'south_america' },
  { code: 'uy', region: 'south_america' },
  { code: 'py', region: 'south_america' },
  { code: 'bo', region: 'south_america' },
  { code: 'br', region: 'south_america' },
  { code: 'sr', region: 'south_america' },
  { code: 'es', region: 'europe' },
  { code: 'pt', region: 'europe' },
  { code: 'fr', region: 'europe' },
  { code: 'de', region: 'europe' },
  { code: 'gb', region: 'europe' },
  { code: 'it', region: 'europe' },
  { code: 'nl', region: 'europe' },
  { code: 'in', region: 'asia_pacific' },
  { code: 'au', region: 'asia_pacific' },
  { code: 'nz', region: 'asia_pacific' },
  { code: 'jp', region: 'asia_pacific' },
  { code: 'ph', region: 'asia_pacific' },
  { code: 'sg', region: 'asia_pacific' },
];

export const CONTEST_COUNTRY_REGION_ORDER: ContestCountryRegion[] = [
  'north_america',
  'central_america',
  'caribbean',
  'south_america',
  'europe',
  'asia_pacific',
];

const LEGACY_ALL_ALIASES = new Set([
  'all',
  'todos',
  'todos los paises',
  'todos los países',
  'all countries',
  'todas las regiones',
  'global',
  'worldwide',
]);

const LEGACY_COUNTRY_ALIASES: Record<string, string> = {
  canada: 'ca',
  'united states': 'us',
  'estados unidos': 'us',
  'united kingdom': 'gb',
  uk: 'gb',
  spain: 'es',
  espana: 'es',
  españa: 'es',
  mexico: 'mx',
  méxico: 'mx',
  brazil: 'br',
  brasil: 'br',
  surinam: 'sr',
  suriname: 'sr',
};

export function getAllConfiguredCountryCodes(): string[] {
  return CONTEST_COUNTRY_OPTIONS.map((country) => country.code);
}

export function normalizeCountryCode(code: string): string {
  const trimmed = String(code || '').trim().toLowerCase();
  if (!trimmed) return '';
  if (LEGACY_ALL_ALIASES.has(trimmed)) return ALL_COUNTRIES_VALUE;
  return LEGACY_COUNTRY_ALIASES[trimmed] || trimmed;
}

export function parseAllowedCountries(raw: unknown): string[] {
  let values: unknown[] = [];

  if (Array.isArray(raw)) {
    values = raw;
  } else if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) {
      values = [];
    } else {
      try {
        const parsed = JSON.parse(trimmed);
        values = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        values = trimmed.includes(',')
          ? trimmed.split(',').map((part) => part.trim())
          : [trimmed];
      }
    }
  } else if (raw) {
    values = [raw];
  }

  const normalized = values
    .map((value) => normalizeCountryCode(String(value)))
    .filter(Boolean);

  if (normalized.length === 0) {
    return [ALL_COUNTRIES_VALUE];
  }

  if (normalized.includes(ALL_COUNTRIES_VALUE)) {
    return [ALL_COUNTRIES_VALUE];
  }

  return [...new Set(normalized)];
}

export function sanitizeAllowedCountries(raw: unknown): string[] {
  const parsed = parseAllowedCountries(raw);
  if (isAllCountriesSelection(parsed)) {
    return [ALL_COUNTRIES_VALUE];
  }

  const validCodes = new Set(getAllConfiguredCountryCodes());
  const specific = parsed.filter((code) => validCodes.has(code));
  return specific.length > 0 ? specific : [ALL_COUNTRIES_VALUE];
}

export function isAllCountriesSelection(countries: unknown): boolean {
  const parsed = parseAllowedCountries(countries);
  return parsed.length === 1 && parsed[0] === ALL_COUNTRIES_VALUE;
}

export function expandAllCountriesSelection(countries: unknown): string[] {
  if (isAllCountriesSelection(countries)) {
    return getAllConfiguredCountryCodes();
  }
  return sanitizeAllowedCountries(countries);
}

export function normalizeCountrySelection(previous: string[], next: string[]): string[] {
  const prev = sanitizeAllowedCountries(previous);
  const selected = next.map((code) => normalizeCountryCode(code)).filter(Boolean);

  if (selected.length === 0) {
    return [ALL_COUNTRIES_VALUE];
  }

  const added = selected.filter((code) => !prev.includes(code));

  if (added.includes(ALL_COUNTRIES_VALUE)) {
    return [ALL_COUNTRIES_VALUE];
  }

  if (prev.includes(ALL_COUNTRIES_VALUE) && selected.some((code) => code !== ALL_COUNTRIES_VALUE)) {
    return sanitizeAllowedCountries(selected.filter((code) => code !== ALL_COUNTRIES_VALUE));
  }

  const withoutAll = selected.filter((code) => code !== ALL_COUNTRIES_VALUE);
  return sanitizeAllowedCountries(withoutAll);
}

export function validateAllowedCountriesSelection(countries: unknown): { valid: boolean; error?: string } {
  const parsed = sanitizeAllowedCountries(countries);
  if (parsed.length === 0) {
    return { valid: false, error: 'Select at least one country or All Countries' };
  }
  return { valid: true };
}

export function isCountryEligible(participantCountry: string, allowedCountries: unknown): boolean {
  if (isAllCountriesSelection(allowedCountries)) return true;
  const allowed = sanitizeAllowedCountries(allowedCountries);
  const code = normalizeCountryCode(participantCountry);
  return code ? allowed.includes(code) : false;
}

export function formatAllowedCountriesLabel(
  countries: unknown,
  translate: (key: string) => string
): string {
  const parsed = sanitizeAllowedCountries(countries);
  if (isAllCountriesSelection(parsed)) {
    return translate('eligibilityinfo.all_countries');
  }

  return parsed
    .map((code) => {
      const label = translate(`eligibilityinfo.${code}`);
      return label === `eligibilityinfo.${code}` ? code.toUpperCase() : label;
    })
    .join(', ');
}

export function getCountriesByRegion(region: ContestCountryRegion): ContestCountryOption[] {
  return CONTEST_COUNTRY_OPTIONS.filter((country) => country.region === region);
}
